// @ts-check

import { createContext, useContext, useEffect, useRef, useState } from "react";
import { DicomImageUploader } from "./utils";
import { randomUUID } from "@minohealth/common/utils/random";
import { useSelector } from "react-redux";
import { SSE } from "sse.js";
import axios from "axios";

/**
 * @param {Object} props
 * @param {React.ReactNode} [props.children]
 */
export function DicomUploaderManagerProvider({ children }) {
  // @ts-ignore
  const { user } = useSelector(state => state.app.datastate)

  /** @type {ReactState<DicomFileInfo[]>} */
  const [dicomFileInfoList, setDicomFileInfoList] = useState([])

  const [advancedMode, setAdvancedMode] = useState(true)

  const sseRef = useRef(/** @type {InstanceType<typeof SSE> | null} */(null))

  // Fetch pending_info records from the server and seed them into local state on mount
  useEffect(() => {
    if (!user?.userToken) return

    axios.get("pacs/pending", {
      baseURL: import.meta.env.VITE_SOCKET_URL,
      headers: { Authorization: `Bearer ${user.userToken}` },
    }).then(({ data }) => {
      const serverItems = data.data ?? []
      if (serverItems.length === 0) return

      setDicomFileInfoList(prev => {
        const existingPacsIds = new Set(prev.map(f => f.pacsId).filter(Boolean))

        const seeded = serverItems
          .filter(item => !existingPacsIds.has(item.pacsId))
          .map(item => {
            const uploader = new DicomImageUploader()
            return {
              id: randomUUID(),
              file: { name: item.fileName },
              uploadStatus: DicomImageUploader.Status.SUCCESS,
              progress: 100,
              advancedMode: item.advancedMode ?? false,
              pacsId: item.pacsId,
              jobStatus: 'pending_info',
              missingFields: item.missingFields,
              extractedFields: item.extractedFields,
              jobError: null,
              jobRetryAttempt: null,
              isServerSeeded: true,
              uploader,
              promise: null,
              controller: new AbortController(),
              result: { data: { pacsId: item.pacsId, missingFields: item.missingFields, extractedFields: item.extractedFields, image: item.image }, error: null },
            }
          })

        return [...seeded, ...prev]
      })
    }).catch(() => {
      // Non-fatal — user still sees any files uploaded in this session
    })
  }, [user?.userToken])

  // Open SSE connection when user is available; close on unmount
  useEffect(() => {
    if (!user?.userToken) return

    const source = new SSE(`${import.meta.env.VITE_SOCKET_URL}/pacs/events`, {
      headers: { Authorization: `Bearer ${user.userToken}` },
    })

    source.addEventListener("message", (/** @type {MessageEvent} */ event) => {
      try {
        const payload = JSON.parse(event.data)
        if (payload.type === "status" && payload.pacsId) {
          setDicomFileInfoList(prev =>
            prev.map(fileInfo => {
              if (fileInfo.pacsId !== payload.pacsId) return fileInfo
              return {
                ...fileInfo,
                jobStatus: payload.status,
                jobError: payload.error ?? fileInfo.jobError,
                jobRetryAttempt: payload.attempt ?? fileInfo.jobRetryAttempt,
              }
            })
          )
        }
      } catch (_) {}
    })

    source.stream()
    sseRef.current = source

    return () => {
      source.close()
      sseRef.current = null
    }
  }, [user?.userToken])

  useEffect(() => {
    const eventController = new AbortController()

    for (const fileInfo of dicomFileInfoList) {
      if (fileInfo.promise == null) continue

      const compositeSignal = getCompositeAbortSignal([
        eventController.signal,
        fileInfo.controller.signal
      ])

      fileInfo.promise
        .then((data) => {
          setDicomFileInfoList(
            FileInfoListActions.SET_REGISTER_RESULT(fileInfo.id, data, null)
          )
        })
        .catch(error => {
          if (error.name === "AbortError") return
          setDicomFileInfoList(
            FileInfoListActions.SET_REGISTER_RESULT(fileInfo.id, null, error)
          )
        })

      fileInfo.uploader.addEventListener("reset", () => {
        updateFileInfoStatus(fileInfo.id)
      }, { signal: compositeSignal })

      fileInfo.uploader.addEventListener("upload-start", () => {
        updateFileInfoStatus(fileInfo.id)
      }, { signal: compositeSignal })

      fileInfo.uploader.addEventListener("upload-progress", event => {
        updateFileInfoStatus(fileInfo.id, undefined, event.detail.progress)
      }, { signal: compositeSignal })

      fileInfo.uploader.addEventListener("upload-success", event => {
        // uploadStatus goes SUCCESS once register-upload returns; jobStatus takes over
        setDicomFileInfoList(
          FileInfoListActions.UPDATE_UPLOAD_RESULT(fileInfo.id, { data: event.detail.result, error: null })
        )
      }, { signal: compositeSignal })

      fileInfo.uploader.addEventListener("upload-error", event => {
        setDicomFileInfoList(
          FileInfoListActions.UPDATE_UPLOAD_RESULT(fileInfo.id, { data: null, error: event.detail.error })
        )
      }, { signal: compositeSignal })

      fileInfo.uploader.addEventListener("upload-aborted", () => {
        updateFileInfoStatus(fileInfo.id)
      }, { signal: compositeSignal })
    }

    return () => eventController.abort()
  }, [dicomFileInfoList])

  /**
   * @param {string} id
   * @param {import("./utils").DicomImageUploaderStatus} [status]
   * @param {number} [progress]
   */
  function updateFileInfoStatus(id, status, progress) {
    setDicomFileInfoList(FileInfoListActions.UPDATE_STATUS(id, status, progress))
  }

  /**
   * @param {string} id
   * @param {DicomFileInfo["result"]} result
   */
  function updateFileInfoResult(id, result) {
    setDicomFileInfoList(FileInfoListActions.UPDATE_UPLOAD_RESULT(id, result))
  }

  /** @param {File} file */
  function addFile(file) {
    const id = randomUUID()
    const controller = new AbortController()
    const uploader = new DicomImageUploader()
    const capturedAdvancedMode = advancedMode
    const promise = startUpload(file, uploader, controller, capturedAdvancedMode)

    setDicomFileInfoList(prev => ([
      ...prev,
      {
        id,
        file,
        uploadStatus: uploader.status,
        progress: null,
        advancedMode: capturedAdvancedMode,
        pacsId: null,
        jobStatus: null,
        missingFields: [],
        extractedFields: {},
        jobError: null,
        jobRetryAttempt: null,
        isServerSeeded: false,
        uploader,
        promise,
        controller,
        result: null,
      }
    ]))
  }

  /**
   * @param {DicomFileInfo["file"]} file
   * @param {DicomFileInfo["uploader"]} uploader
   * @param {DicomFileInfo["controller"]} controller
   * @param {boolean} capturedAdvancedMode
   * @param {DicomFileInfo["result"]} [result]
   */
  async function startUpload(file, uploader, controller, capturedAdvancedMode, result) {
    if (user?.userToken == null) {
      return Promise.reject(new Error("User token is required"))
    }

    const uploadPromise = result?.data != null
      ? Promise.resolve(result.data)
      : uploader.upload(file, user.userToken, capturedAdvancedMode, { signal: controller.signal })
    return uploadPromise
  }

  /** @param {string} id */
  function removeFile(id) {
    setDicomFileInfoList(prev => {
      const fileInfo = prev.find(f => f.id === id)
      if (fileInfo == null) return prev
      fileInfo.controller.abort()
      return prev.filter(f => f.id !== id)
    })
  }

  /** @param {string} id */
  function retryUpload(id) {
    const fileInfo = dicomFileInfoList.find(f => f.id === id)
    if (fileInfo == null) return
    if (fileInfo.uploadStatus === "success") return
    if (fileInfo.promise != null) return

    const controller = new AbortController()
    const promise = startUpload(fileInfo.file, fileInfo.uploader, controller, fileInfo.advancedMode, fileInfo.result)

    setDicomFileInfoList(FileInfoListActions.UPDATE_PROMISE(id, promise, controller))
  }

  /**
   * Submit missing fields for a pending_info Pacs record.
   * @param {string} pacsId
   * @param {{ modality?: string, patientAge?: string, patientGender?: string, reasonForExam?: string }} fields
   */
  async function provideInfo(pacsId, fields) {
    if (!user?.userToken) throw new Error("User token is required")

    try {
      await axios.post("pacs/provide-info", { pacsId, fields }, {
        baseURL: import.meta.env.VITE_SOCKET_URL,
        headers: { Authorization: `Bearer ${user.userToken}`, "Content-Type": "application/json" },
      })
    } catch (error) {
      // The server's reason, such as too few credits, is shown in the form.
      const reason = error?.response?.data?.error
      throw new Error(typeof reason === "string" ? reason : "Could not submit the details. Try again.", { cause: error })
    }

    // Optimistically update jobStatus to queued; SSE will confirm
    setDicomFileInfoList(prev =>
      prev.map(fileInfo => {
        if (fileInfo.pacsId !== pacsId) return fileInfo
        return { ...fileInfo, jobStatus: "queued", missingFields: [] }
      })
    )
  }

  const leanFileInfoList = dicomFileInfoList.map(fileInfo => ({
    id: fileInfo.id,
    file: fileInfo.file,
    uploadStatus: fileInfo.uploadStatus,
    progress: fileInfo.progress,
    advancedMode: fileInfo.advancedMode,
    pacsId: fileInfo.pacsId,
    jobStatus: fileInfo.jobStatus,
    missingFields: fileInfo.missingFields,
    extractedFields: fileInfo.extractedFields,
    jobError: fileInfo.jobError,
    jobRetryAttempt: fileInfo.jobRetryAttempt,
    isServerSeeded: fileInfo.isServerSeeded,
    isComplete: fileInfo.uploadStatus === "success" || fileInfo.uploadStatus === "error",
    error: fileInfo.result?.error ?? null,
    result: fileInfo.result,
  }))

  return (
    <dicomUploaderManagerContext.Provider
      value={{
        fileInfoList: leanFileInfoList,
        advancedMode,
        setAdvancedMode,
        addFile,
        removeFile,
        retryUpload,
        provideInfo,
      }}
    >
      {children}
    </dicomUploaderManagerContext.Provider>
  )
}

export function useDicomUploaderManager() {
  return useContext(dicomUploaderManagerContext)
}

const dicomUploaderManagerContext = createContext({
  /** @type {LeanDicomFileInfo[]} */
  fileInfoList: [],
  advancedMode: true,
  /** @param {boolean} _v */
  setAdvancedMode(_v) {},
  /** @param {File} file */
  addFile(file) {},
  /** @param {string} id */
  removeFile(id) {},
  /** @param {string} id */
  retryUpload(id) {},
  /**
   * @param {string} _pacsId
   * @param {object} _fields
   */
  async provideInfo(_pacsId, _fields) {},
})

/** @satisfies {Record<string, FileInfoListActionCallback>} */
const FileInfoListActions = {
  /**
   * @param {DicomFileInfo["id"]} id
   * @param {DicomFileInfo["promise"]} promise
   * @param {DicomFileInfo["controller"]} controller
   */
  UPDATE_PROMISE(id, promise, controller) {
    return prevFileInfoList => (
      prevFileInfoList.map(fileInfo => {
        if (fileInfo.id !== id) return fileInfo
        return {
          ...fileInfo,
          uploadStatus: fileInfo.uploader.status,
          promise,
          controller,
          result: fileInfo.result?.data != null ? fileInfo.result : null,
        }
      })
    )
  },

  /**
   * @param {DicomFileInfo["id"]} id
   * @param {DicomFileInfo["result"]} result
   */
  UPDATE_UPLOAD_RESULT(id, result) {
    return prevFileInfoList => (
      prevFileInfoList.map(fileInfo => {
        if (fileInfo.id !== id) return fileInfo
        return {
          ...fileInfo,
          uploadStatus: fileInfo.uploader.status,
          promise: null,
          result,
        }
      })
    )
  },

  /**
   * Called when register-upload resolves/rejects.
   * On success, stamps pacsId, missingFields, extractedFields onto the file info.
   *
   * @param {DicomFileInfo["id"]} id
   * @param {{ pacsId: string, missingFields: string[], extractedFields: object } | null} data
   * @param {Error | null} error
   */
  SET_REGISTER_RESULT(id, data, error) {
    return prevFileInfoList => (
      prevFileInfoList.map(fileInfo => {
        if (fileInfo.id !== id) return fileInfo
        return {
          ...fileInfo,
          promise: null,
          uploadStatus: fileInfo.uploader.status,
          pacsId: data?.pacsId ?? fileInfo.pacsId,
          jobStatus: data != null
            ? (data.missingFields.length > 0 ? "pending_info" : "queued")
            : fileInfo.jobStatus,
          missingFields: data?.missingFields ?? fileInfo.missingFields,
          extractedFields: data?.extractedFields ?? fileInfo.extractedFields,
          result: { data, error },
        }
      })
    )
  },

  /**
   * @param {DicomFileInfo["id"]} id
   * @param {import("./utils").DicomImageUploaderStatus} [status]
   * @param {number} [progress]
   */
  UPDATE_STATUS(id, status, progress) {
    return prevFileInfoList => (
      prevFileInfoList.map(fileInfo => {
        if (fileInfo.id !== id) return fileInfo
        return {
          ...fileInfo,
          progress: progress ?? fileInfo.progress,
          uploadStatus: status ?? fileInfo.uploader.status,
        }
      })
    )
  },
}

/**
 * Returns a composite abort signal that aborts when any of the signals abort.
 * @param {AbortSignal[]} signals
 */
function getCompositeAbortSignal(signals) {
  if (AbortSignal.any != null && typeof AbortSignal.any === "function") {
    return AbortSignal.any(signals)
  }

  const compositeController = new AbortController()
  for (const signal of signals) {
    signal.addEventListener("abort", (event) => {
      const target = event.target
      if (!(target instanceof AbortSignal)) return compositeController.abort()
      compositeController.abort(target.reason)
    })
  }
  return compositeController.signal
}

/**
 * @typedef {Object} DicomFileInfo
 * @property {string} id
 * @property {File} file
 * @property {import("./utils").DicomImageUploaderStatus} uploadStatus
 * @property {number | null} progress
 * @property {boolean} advancedMode
 * @property {string | null} pacsId
 * @property {JobStatus | null} jobStatus
 * @property {string[]} missingFields
 * @property {object} extractedFields
 * @property {string | null} jobError
 * @property {number | null} jobRetryAttempt
 * @property {boolean} isServerSeeded
 * @property {DicomImageUploader} uploader
 * @property {Promise<any>} [promise]
 * @property {UploadResult | null} result
 * @property {AbortController} controller
 */

/**
 * @typedef {'pending_info'|'queued'|'processing'|'report_generating'|'retrying'|'complete'|'error'} JobStatus
 */

/**
 * @typedef {typeof dicomUploaderManagerContext extends React.Context<infer T> ? T : never} DicomUploaderManagerContext
 */

/**
 * @typedef {Pick<DicomFileInfo, "id"|"file"|"uploadStatus"|"progress"|"advancedMode"|"pacsId"|"jobStatus"|"missingFields"|"extractedFields"|"jobError"|"jobRetryAttempt"|"isServerSeeded"|"result"> & {error: Error | null, isComplete: boolean}} LeanDicomFileInfo
 */

/** @typedef { | {data: any, error: null} | {data: null, error: Error} } UploadResult */

/** @typedef {React.SetStateAction<DicomFileInfo[]>} FileInfoListSetStateAction */
/** @typedef {(...args: any[]) => FileInfoListSetStateAction} FileInfoListActionCallback */
